import classNames from 'classnames'
import { getNavbarHeight } from '../utils/layout'
import { useRef, useEffect } from 'react'
import throttle from 'lodash-es/throttle'
const placeholderStyles = {
  zIndex: 'calc(var(--bs-zindex-sticky) - 1)'
} as React.CSSProperties

type StickyProps = {
  children: React.ReactNode
  className?: string
  alignment?: 'top' | 'bottom'
  isSticky?: boolean
}

function Sticky(props: StickyProps) {
  const placeholderRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const isTopAlign = (props.alignment ?? 'top') === 'top'
  const isStickyEnabled = props.isSticky ?? true

  useEffect(() => {
    if (!placeholderRef.current || !contentRef.current) return
    const placeholderElement = placeholderRef.current
    const contentElement = contentRef.current

    let lastSticky = false

    function applySticky(state: boolean) {
      if (state) {
        placeholderElement.style.minHeight = `${contentElement.offsetHeight}px`
      } else {
        placeholderElement.style.minHeight = ''
      }
      placeholderElement.classList.toggle('is-sticky', state)
    }

    const handleScroll = throttle(() => {
      const navbarHeight = getNavbarHeight()
      const prevClassList = placeholderElement.className
      placeholderElement.classList.remove(
        'sticky--on-header',
        'sticky--on-bottom'
      )
      const placeholderRect = placeholderElement?.getBoundingClientRect()
      placeholderElement.className = prevClassList
      let isSticky = false
      if (isTopAlign) {
        isSticky = (placeholderRect?.top ?? 0) <= navbarHeight
      } else {
        isSticky = (placeholderRect?.bottom ?? 0) >= window.innerHeight
      }

      if (isSticky === lastSticky) return

      lastSticky = isSticky

      applySticky(isSticky)
    }, 200)

    if (isStickyEnabled) {
      handleScroll()
      window.addEventListener('scroll', handleScroll, { passive: true })
    }

    return () => {
      applySticky(false)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isTopAlign, isStickyEnabled])

  return (
    <div
      ref={placeholderRef}
      className={classNames(
        { 'sticky--on-header': isTopAlign && isStickyEnabled },
        { 'sticky--on-bottom': !isTopAlign && isStickyEnabled },
        props.className
      )}
      style={placeholderStyles}
    >
      <div ref={contentRef}>{props.children}</div>
    </div>
  )
}

export default Sticky
